import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { AlertCircle, BookOpen, Newspaper, HardHat, Phone, Users, Landmark } from "lucide-react";

const DEFAULT_HERO =
  "https://images.unsplash.com/photo-1787637607060-10fdc42ec51d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxoaXN0b3JpYyUyMGhlcml0YWdlJTIwYnJpY2slMjB0b3duJTIwaGFsbCUyMGFyY2hpdGVjdHVyZSUyMGV4dGVyaW9yfGVufDB8fHx8MTc5MTAyMTE5MHww&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const [hero, setHero] = useState(null);
  const [importantMsgs, setImportantMsgs] = useState([]);
  const [rules, setRules] = useState([]);

  useEffect(() => {
    (async () => {
      const [h, im, rr] = await Promise.all([
        api.get("/hero").catch(() => ({ data: {} })),
        api.get("/important-messages").catch(() => ({ data: [] })),
        api.get("/rules").catch(() => ({ data: [] })),
      ]);
      setHero(h.data?.file_id ? fileUrl(h.data.file_id) : DEFAULT_HERO);
      setImportantMsgs(Array.isArray(im.data) ? im.data : []);
      setRules(rr.data || []);
    })();
  }, []);

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
    } catch {
      return "";
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-stone-200 shadow-[0_12px_30px_-8px_rgba(41,34,29,0.25)]">
        {hero && (
          <img
            src={hero}
            alt="Salford Town Hall"
            className="h-[640px] w-full object-cover sm:h-[840px]"
          />
        )}
        {!hero && <div className="h-[640px] w-full sm:h-[840px]" aria-hidden="true" />}
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950/70 via-stone-900/15 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10">
          <div className="text-amber-100/80 text-xs font-mono tracking-widest uppercase">Welcome</div>
          <h1 className="mt-1 font-serif text-4xl font-semibold text-white sm:text-5xl">
            Salford Town Hall
          </h1>
          <p className="mt-1 max-w-xl text-stone-100/90">
            A home for our residents and leaseholders — stay informed, stay connected.
          </p>
        </div>
      </div>

      {/* Important Messages */}
      {importantMsgs.length > 0 && (
        <div data-testid="important-message-banner" className="my-8 space-y-4">
          {importantMsgs.map((msg) => (
            <div
              key={msg.id}
              className="rounded-r-2xl border-l-4 border-amber-600 bg-amber-50 p-6 shadow-sm"
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
                <div className="flex-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="font-serif text-xl font-semibold text-stone-900">Important Message</h3>
                    <span className="text-xs text-stone-600">
                      {formatDate(msg.created_at)}
                      {msg.edited_at && (
                        <span className="ml-1 italic text-stone-500">· edited {formatDate(msg.edited_at)}</span>
                      )}
                    </span>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap leading-relaxed text-stone-800">{msg.text}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Category tiles */}
      <section className="mt-10">
        <h2 className="font-serif text-2xl font-semibold text-stone-900">Browse the portal</h2>
        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <Link to="/faq/residents" data-testid="tile-faq-residents" className="category-tile btn-moss">
            <Users className="h-7 w-7 opacity-90" />
            <div>
              <div className="font-serif text-2xl font-semibold">FAQs for Residents</div>
              <p className="mt-1 text-sm text-white/85">Day-to-day questions, services, and building life</p>
            </div>
          </Link>
          <Link to="/faq/leaseholders" data-testid="tile-faq-leaseholders" className="category-tile btn-chestnut">
            <BookOpen className="h-7 w-7 opacity-90" />
            <div>
              <div className="font-serif text-2xl font-semibold">FAQs for Leaseholders</div>
              <p className="mt-1 text-sm text-white/85">Leasehold matters, service charges and governance</p>
            </div>
          </Link>
          <Link to="/news" data-testid="tile-news" className="category-tile btn-terracotta">
            <Newspaper className="h-7 w-7 opacity-90" />
            <div>
              <div className="font-serif text-2xl font-semibold">Latest News</div>
              <p className="mt-1 text-sm text-white/85">Announcements and community updates</p>
            </div>
          </Link>
          <Link to="/projects" data-testid="tile-projects" className="category-tile btn-amber">
            <HardHat className="h-7 w-7 opacity-90" />
            <div>
              <div className="font-serif text-2xl font-semibold">Building Projects</div>
              <p className="mt-1 text-sm text-white/85">Works in progress and upcoming improvements</p>
            </div>
          </Link>
          <Link to="/history" data-testid="tile-history" className="category-tile btn-chestnut">
            <Landmark className="h-7 w-7 opacity-90" />
            <div>
              <div className="font-serif text-2xl font-semibold">History</div>
              <p className="mt-1 text-sm text-white/85">The story of Salford Town Hall</p>
            </div>
          </Link>
          <Link to="/contact" data-testid="tile-contact" className="category-tile btn-slate">
            <Phone className="h-7 w-7 opacity-90" />
            <div>
              <div className="font-serif text-2xl font-semibold">Managing Agent</div>
              <p className="mt-1 text-sm text-white/85">Contact details and office hours</p>
            </div>
          </Link>
        </div>
      </section>

      {/* Rules of the Building */}
      <section
        data-testid="rules-of-building-section"
        className="mt-14 rounded-3xl border border-stone-200 bg-stone-50 p-6 sm:p-10"
      >
        <h2 className="font-serif text-2xl font-semibold text-stone-900">Rules of the Building</h2>
        <p className="mt-1 text-sm text-stone-600">Current house rules and updates from management</p>
        {rules.length === 0 ? (
          <p className="mt-4 text-stone-700">
            No building rules have been posted yet. Please check back soon.
          </p>
        ) : (
          <div className="mt-5 space-y-4">
            {rules.map((r) => (
              <div key={r.id} className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-stone-200">
                <div className="mb-1 text-xs font-mono uppercase tracking-widest text-stone-500">
                  {formatDate(r.created_at)}
                </div>
                <p className="whitespace-pre-wrap leading-relaxed text-stone-800">{r.text}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
